// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Power, PowerOff, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { type UserRow, useToggleUserActiveMutation } from "@/api/hooks/use-users";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ToggleUserActiveDialogProps {
  user: UserRow | null;
  onClose: () => void;
}

export function ToggleUserActiveDialog({ user, onClose }: ToggleUserActiveDialogProps) {
  const { t } = useTranslation("back-office");
  const lastUser = useRef(user);
  if (user) lastUser.current = user;
  const display = user ?? lastUser.current;
  const toggleActive = useToggleUserActiveMutation(display?.publicId ?? "");

  async function onSubmit() {
    if (!display) return;
    const newActive = !display.isActive;
    try {
      await toggleActive.mutateAsync(newActive);
      toast.success(
        newActive
          ? t("users.notifications.activated", { name: display.fullName ?? display.email })
          : t("users.notifications.deactivated", { name: display.fullName ?? display.email }),
      );
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={() => onClose()}>
      <DialogContent
        id="dialog-toggle-user-status"
        className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-row">
          <WizardPane
            variant={display?.isActive ? "destructive" : "default"}
            icon={display?.isActive ? PowerOff : Power}
          />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>
                {display?.isActive ? t("users.dialog.deactivateTitle") : t("users.dialog.activateTitle")}
              </DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {display?.isActive ? t("users.dialog.deactivatePrompt") : t("users.dialog.activatePrompt")}{" "}
                    {display?.fullName ? (
                      <>
                        <span className="font-semibold text-foreground">{display.fullName}</span> ({display.email})
                      </>
                    ) : (
                      <span className="font-semibold text-foreground">{display?.email}</span>
                    )}
                    ?
                  </p>
                  {display?.isActive && (
                    <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="text-sm">{t("users.dialog.deactivateWarning")}</span>
                    </div>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant={display?.isActive ? "destructive" : "default"}
            className={
              display?.isActive
                ? ""
                : "bg-green-700 text-white hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600"
            }
            disabled={toggleActive.isPending}
            onClick={onSubmit}
          >
            {toggleActive.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : display?.isActive ? (
              <PowerOff className="h-4 w-4" />
            ) : (
              <Power className="h-4 w-4" />
            )}
            {display?.isActive ? t("users.dialog.deactivateConfirm") : t("users.dialog.activateConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
